import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Tracks which users are currently online based on MQTT presence publications
 * and recent activity timestamps.
 */
const useOnlineStatus = ({ profile, selectedSession, isMountedRef }) => {
    const [onlineUsers, setOnlineUsers] = useState(new Map());
    const onlineCheckIntervalRef = useRef(null);

    // ─── Check / Update helpers ───────────────────────────────────────────────

    const checkUserOnlineStatus = useCallback(
        (userId, lastActivity = null) => {
            if (!userId) return false;

            if (onlineUsers.has(userId)) return onlineUsers.get(userId);

            if (lastActivity) {
                const timeDiff = Date.now() - new Date(lastActivity).getTime();
                return timeDiff < 10 * 60 * 1000; // 10 min
            }

            if (userId === profile?.pess_id_pessoa) return true;

            return false;
        },
        [onlineUsers, profile?.pess_id_pessoa],
    );

    const updateUsersOnlineStatus = useCallback(
        (userIds, activityData = {}) => {
            if (!userIds || userIds.length === 0) return;

            setOnlineUsers((prevOnlineUsers) => {
                const updated = new Map(prevOnlineUsers);

                for (const userId of userIds) {
                    const lastActivity = activityData[userId];

                    if (lastActivity) {
                        const timeDiff = Date.now() - new Date(lastActivity).getTime();
                        updated.set(userId, timeDiff < 10 * 60 * 1000);
                    } else if (!updated.has(userId)) {
                        updated.set(userId, false);
                    }

                    if (userId === profile?.pess_id_pessoa) {
                        updated.set(userId, true);
                    }
                }

                return updated;
            });
        },
        [profile?.pess_id_pessoa],
    );

    const getSessionOnlineStatus = useCallback(
        (session) => {
            if (!session) return { usuario: false, responsavel: false };

            const usuarioId = session.usuario?.id || session.usuario?.pess_id_pessoa;
            const responsavelId = session.responsavel?.usuario?.pess_id_pessoa;
            const usuarioLastActivity = session.last_message?.data_mensagem || session.data_criacao;
            const responsavelLastActivity = session.responsavel?.last_activity;

            return {
                usuario: usuarioId ? checkUserOnlineStatus(usuarioId, usuarioLastActivity) : false,
                responsavel: responsavelId ? checkUserOnlineStatus(responsavelId, responsavelLastActivity) : false,
            };
        },
        [checkUserOnlineStatus],
    );

    // Keep a stable ref to updateUsersOnlineStatus so intervals don't stale-close
    const updateUsersOnlineStatusRef = useRef();
    updateUsersOnlineStatusRef.current = updateUsersOnlineStatus;

    // ─── Collect activity data for a session ──────────────────────────────────

    const collectSessionActivityData = useCallback(
        (session) => {
            const userIds = [];
            const activityData = {};

            const usuarioId = session?.usuario?.id || session?.usuario?.pess_id_pessoa;
            if (usuarioId) {
                userIds.push(usuarioId);
                activityData[usuarioId] = session.last_message?.data_mensagem || session.data_criacao;
            }

            const responsavelId = session?.responsavel?.usuario?.pess_id_pessoa;
            if (responsavelId) {
                userIds.push(responsavelId);
                activityData[responsavelId] =
                    session.responsavel?.last_activity ||
                    (session.status === 'PENDENTE_HUMANO' ? new Date().toISOString() : null);
            }

            if (profile?.pess_id_pessoa) {
                userIds.push(profile.pess_id_pessoa);
                activityData[profile.pess_id_pessoa] = new Date().toISOString();
            }

            return { userIds: [...new Set(userIds)], activityData };
        },
        [profile?.pess_id_pessoa],
    );

    // ─── Periodic updates ─────────────────────────────────────────────────────

    const startOnlineStatusUpdates = useCallback(
        (session) => {
            if (onlineCheckIntervalRef.current) clearInterval(onlineCheckIntervalRef.current);

            onlineCheckIntervalRef.current = setInterval(() => {
                if (!isMountedRef.current || !session) return;
                const { userIds, activityData } = collectSessionActivityData(session);
                if (userIds.length > 0 && updateUsersOnlineStatusRef.current) {
                    updateUsersOnlineStatusRef.current(userIds, activityData);
                }
            }, 30000);
        },
        [isMountedRef, collectSessionActivityData],
    );

    const stopOnlineStatusUpdates = useCallback(() => {
        if (onlineCheckIntervalRef.current) {
            clearInterval(onlineCheckIntervalRef.current);
            onlineCheckIntervalRef.current = null;
        }
    }, []);

    // ─── Effect: update immediately when selected session changes ─────────────

    useEffect(() => {
        if (selectedSession) {
            const { userIds, activityData } = collectSessionActivityData(selectedSession);
            if (userIds.length > 0 && updateUsersOnlineStatusRef.current) {
                updateUsersOnlineStatusRef.current(userIds, activityData);
            }
            startOnlineStatusUpdates(selectedSession);
        } else {
            stopOnlineStatusUpdates();
        }

        return () => stopOnlineStatusUpdates();
    }, [selectedSession, startOnlineStatusUpdates, stopOnlineStatusUpdates, collectSessionActivityData]);

    return {
        onlineUsers,
        setOnlineUsers,
        checkUserOnlineStatus,
        updateUsersOnlineStatus,
        getSessionOnlineStatus,
        startOnlineStatusUpdates,
        stopOnlineStatusUpdates,
    };
};

export default useOnlineStatus;
